import { User } from "firebase/auth";
import { doc, FirestoreDataConverter, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { getFirestoreDB } from "@/firebase";

export type UserProfileData = {
    uid: string;
    isAnonymous: boolean;
    firstName: string;
    phoneNumber?: string;
    phoneVerified: boolean;
    emailAddress?: string;
    isDeleted?: boolean;
    createdAt?: Date;
    modifiedAt?: Date;
    inUserTesting?: boolean;
    userOrg?: string;
    userPreferences?: {
        notifyByEmail: boolean;
        notifyBySMS: boolean;
    };
    acceptedPrivacyPolicy?: boolean;
};

class UserProfile implements UserProfileData {
    _uid: string;

    _firstName: string;

    phoneNumber: string;

    phoneVerified: boolean;

    emailAddress: string;

    isAnonymous: boolean;

    isDeleted: boolean;

    createdAt: Date;

    modifiedAt: Date;

    inUserTesting: boolean;

    userOrg: string;

    userPreferences?: { notifyByEmail: boolean; notifyBySMS: boolean } | undefined;

    acceptedPrivacyPolicy: boolean;

    constructor(data: UserProfileData) {
        this._uid = data.uid;
        this._firstName = data.firstName;
        this.phoneNumber = data.phoneNumber || "";
        this.phoneVerified = data.phoneVerified;
        this.emailAddress = data.emailAddress || "";
        this.isAnonymous = data.isAnonymous;
        this.isDeleted = data.isDeleted !== undefined ? data.isDeleted : false;
        this.createdAt = data.createdAt || new Date();
        this.modifiedAt = data.createdAt || new Date();
        this.inUserTesting = data.inUserTesting || false;
        this.userOrg = data.userOrg || "";
        this.userPreferences = data.userPreferences ?? {
            notifyByEmail: false,
            notifyBySMS: false,
        };
        this.acceptedPrivacyPolicy = data.acceptedPrivacyPolicy ?? false;
    }

    get uid(): string {
        return this._uid;
    }

    get firstName(): string {
        return this._firstName;
    }

    set firstName(newFirstName: string) {
        this.firstName = newFirstName;
        this.modifiedAt = new Date();
    }
}

export const userProfileConverter: FirestoreDataConverter<UserProfile> = {
    toFirestore(data: UserProfile) {
        return {
            firstName: data.firstName,
            phoneNumber: data.phoneNumber,
            phoneVerified: data.phoneVerified,
            emailAddress: data.emailAddress,
            uid: data.uid,
            isAnonymous: data.isAnonymous,
            isDeleted: data.isDeleted,
            createdAt: data.createdAt,
            modifiedAt: data.modifiedAt,
            inUserTesting: data.inUserTesting,
            userOrg: data.userOrg,
            userPreferences: data.userPreferences,
            acceptedPrivacyPolicy: data.acceptedPrivacyPolicy,
        };
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);

        return new UserProfile({
            firstName: data.firstName,
            phoneNumber: data.phoneNumber,
            phoneVerified: data.phoneVerified,
            emailAddress: data.emailAddress,
            uid: data.uid,
            isAnonymous: data.isAnonymous,
            isDeleted: data.isDeleted,
            createdAt: data.createdAt,
            modifiedAt: data.modifiedAt,
            inUserTesting: data.inUserTesting,
            userOrg: data.userOrg,
            userPreferences: data.userPreferences,
            acceptedPrivacyPolicy: data.acceptedPrivacyPolicy,
        });
    },
};

export async function userProfileExists(uid: string): Promise<boolean> {
    const db = getFirestoreDB();

    const userProfile = await getDoc(doc(db, "userProfile", uid));

    return userProfile.exists();
}

export async function createUserProfile(data: UserProfileData) {
    const db = getFirestoreDB();

    const userProfile = new UserProfile(data);
    await setDoc(doc(db, "userProfile", data.uid).withConverter(userProfileConverter), userProfile);
}

export async function createUserProfileFromUser(user: User, override?: Partial<UserProfileData>) {
    const userData: UserProfileData = {
        uid: user.uid,
        firstName: user.displayName || "",
        phoneNumber: user.phoneNumber || "",
        phoneVerified: false,
        emailAddress: user.email || "",
        isAnonymous: user.isAnonymous,
    };

    return createUserProfile({ ...userData, ...override });
}

export async function updateUserPreferences(uid: string, pref: { notifyByEmail: boolean; notifyBySMS: boolean }) {
    const db = getFirestoreDB();
    const ref = doc(db, "userProfile", uid).withConverter(userProfileConverter);
    await updateDoc(ref, {
        userPreferences: pref,
        modifiedAt: new Date(),
    });
}

export async function userAcceptedPrivacyPolicy(uid: string) {
    const db = getFirestoreDB();
    const ref = doc(db, "userProfile", uid).withConverter(userProfileConverter);
    await updateDoc(ref, {
        acceptedPrivacyPolicy: true,
        modifiedAt: new Date(),
    });
}

export default UserProfile;
